import React from "react";
import { graphql, Link } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import Page from "~/models/page";
import Frame from "~/views/layout/frame";
import SEO from "~/views/components/generic/seo";
import structuredData from "~/helpers/structured-data";
import Navigation from "~/views/compositions/navigation";
import Breadcrumbs from "~/views/compositions/breadcrumbs";

type Props = {
  data: any;
};

const AboutPage = ({ data }: Props) => {
  const page = new Page(data.page);
  // ページコンテンツのデータ - Figmaデザインに基づく
  const content = {
    hero: {
      sectionTitle: "Who We Are",
      title: "デジタルプロダクトデザインで\nビジネスの成長を加速させる",
      description:
        "Artefactは、プロダクト戦略とUX/UIデザインを専門とするデジタルプロダクトデザインスタジオです。ユーザー体験の向上とビジネス成果の最大化を両立させるソリューションを提供しています。",
      buttonText: "お問い合わせ",
      buttonLink: "/contact",
    },
    mission: {
      sectionTitle: "Our Mission",
      title: "デジタルプロダクトの可能性を最大限に引き出す",
      description:
        "私たちは、デザインの力でビジネスとユーザーの両方に価値を提供することを使命としています。プロダクトリサーチと戦略的思考に基づいたデザインソリューションで、クライアントのビジネス成長を支援します。",
      points: [
        "ビジネス成果に直結するデザイン戦略",
        "データドリブンなデザイン意思決定",
        "最適な課題解決のためのインタラクション設計",
      ],
    },
    companyInfo: {
      sectionTitle: "Company Information",
      title: "企業情報",
      details: [
        "企業名: 株式会社Artefact (英: Artefact Inc.)",
        "本社所在地: 東京都目黒区駒場 4-6-2 Y-5 yamagataya 501",
        "代表者: 菅沼 祥太郎",
      ],
    },
    footer: {
      companyName: "株式会社Artefact",
      address: "東京都目黒区駒場4-6-2 Y-5 yamagataya 501",
      copyright: "©2025 Artefact Inc.",
    },
  };

  return (
    <Frame>
      <Navigation />
      <Breadcrumbs urls={page.breadcrumb} />

      {/* Mission Section - Following Figma design */}
      <section className="flex gap-8 sm:gap-12 lg:gap-16 items-start px-4 sm:px-6 lg:px-10 py-12 w-full max-w-[1096px] mx-auto">
        <div className="flex flex-col gap-6 sm:gap-8 lg:gap-[31px] w-full sm:w-[480px] lg:w-[576px] text-[#121212]">
          <div className="flex flex-col gap-3 sm:gap-4">
            <p className="font-[800] text-[16px] sm:text-[18px] lg:text-[20px] leading-[28px] sm:leading-[36px] lg:leading-[44px]">
              {content.mission.sectionTitle}
            </p>
            <h2 className="font-[800] text-[24px] sm:text-[32px] lg:text-[40px] leading-[36px] sm:leading-[48px] lg:leading-[74px] palt">
              {content.mission.title}
            </h2>
          </div>
          <p className="font-[400] text-[14px] sm:text-[16px] lg:text-[18px] leading-[1.6] sm:leading-[1.8] lg:leading-[2] palt">
            {content.mission.description}
          </p>
          <div className="flex flex-col gap-3 sm:gap-4 mb-3">
            {content.mission.points.map((point, index) => (
              <div key={index} className="flex items-start gap-3 sm:gap-4">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#121212] rounded-full mt-2 sm:mt-3 flex-shrink-0"></div>
                <p className="font-[400] text-[14px] sm:text-[16px] lg:text-[18px] leading-[1.6] sm:leading-[1.8] lg:leading-[2] palt">
                  {point}
                </p>
              </div>
            ))}
          </div>
          <a
            href="/contact"
            className="inline-flex items-center gap-2.5 px-4 sm:px-5 py-2.5 border border-black rounded-[30px] bg-white w-fit"
          >
            <span className="font-medium text-base sm:text-lg">
              お問い合わせ
            </span>
            <img
              src="/images/contact-arrow.svg"
              alt=""
              className="w-5 h-5 sm:w-6 sm:h-6"
            />
          </a>
        </div>
      </section>

      <div className="bg-neutral-99">
        {/* Company Information Section - Following Figma design */}
        <section className=" flex flex-col gap-6 sm:gap-8 lg:gap-[31px] px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 w-full max-w-[1096px] mx-auto text-[#121212]">
          <div className="flex flex-col gap-3 sm:gap-4 max-w-[1045px]">
            <p className="font-[800] text-[16px] sm:text-[18px] lg:text-[20px] leading-[28px] sm:leading-[36px] lg:leading-[44px]">
              {content.companyInfo.sectionTitle}
            </p>
            <h2 className="font-[800] text-[20px] sm:text-[24px] lg:text-[28px] leading-[32px] sm:leading-[40px] lg:leading-[52px]">
              {content.companyInfo.title}
            </h2>
          </div>
          <div className="font-[400] text-[14px] sm:text-[16px] lg:text-[18px] leading-[1.6] sm:leading-[1.8] lg:leading-[2] palt max-w-[1045px]">
            {content.companyInfo.details.map((detail, index) => (
              <p
                key={index}
                className={
                  index < content.companyInfo.details.length - 1 ? "mb-0" : ""
                }
              >
                {detail}
              </p>
            ))}
          </div>
        </section>
      </div>
    </Frame>
  );
};

export default AboutPage;

export const Head = ({ data }: Props) => {
  const page = new Page(data.page as any);
  const sd = structuredData(page);

  return <SEO page={page} sd={sd} />;
};

export const query = graphql`
  query AboutPage {
    page: pageMdx(
      filter: { frontmatter: { url: { pathname: { eq: "/about-us/" } } } }
    ) {
      id
      frontmatter {
        id
        title
        description
        breadcrumb {
          ...UrlFields
        }
        url {
          ...UrlFields
        }
        type
        releasedAt
        updatedAt
      }
    }
  }
`;
